import { createContext, useContext, useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { langFromPath, stripLang, withLang, RTL_LANGS } from "@/lib/i18nRoutes";

const I18nContext = createContext(null);
const STORAGE_KEY = "kh_lang";

// Canonical English strings — instant render + fallback for any missing key.
export const EN_STRINGS = {
  "banner.shipping": "Express Worldwide Shipping",
  "banner.official": "Official Kentucky Horsewear Reseller",
  "banner.returns": "Free shipping on orders over €100 within Ireland",
  "nav.shop": "Shop",
  "nav.shipping": "Shipping",
  "nav.size_guide": "Size Guide",
  "nav.care_guide": "Care Guide",
  "nav.shop_by_category": "Shop by Category",
  "nav.brands": "Brands",
  "nav.guides": "Guides",
  "header.search_placeholder": "Search Kentucky saddle pads, rugs, grooming…",
  "header.shop_boomerang": "Shop on Boomerang.ie",
  "header.official_reseller": "Official Reseller",
  "header.official_reseller_desc": "Boomerang.ie is the official Irish reseller of Kentucky Horsewear. Ireland & EU shipping.",
  "common.view_product": "View Product",
  "common.add_to_cart": "Add to Cart",
  "common.added": "Added to cart",
  "common.in_stock": "In stock",
  "common.out_of_stock": "Out of stock",
  "common.product_details": "Product details",
  "common.back": "Back",
  "common.load_more": "Load more",
  "common.explore": "Explore",
  "common.view_all": "View all",
  "common.shop_all_boomerang": "Shop all on Boomerang.ie",
  "common.related": "More categories",
  "common.products": "products",
  "trust.shipping": "Express worldwide shipping",
  "trust.authentic": "Official & authentic",
  "trust.returns": "Ireland & EU warehouses",
  "cart.title": "Your Cart",
  "cart.empty_title": "Your cart is empty",
  "cart.empty_desc": "Explore the Kentucky collection to get started.",
  "cart.shop_kentucky": "Shop Kentucky",
  "cart.subtotal": "Subtotal",
  "cart.note": "Shipping & taxes calculated securely at Boomerang checkout.",
  "cart.checkout": "Secure Checkout",
  "cart.redirecting": "Redirecting…",
  "footer.tagline": "Boomerang.ie is the official reseller of Kentucky Horsewear, Dy'on, Velari and Grooming Deluxe. Premium equestrian equipment held in our Ireland & EU warehouses and shipped worldwide with express delivery.",
  "footer.shop": "Shop",
  "footer.brands_help": "Brands & Help",
  "footer.newsletter": "Newsletter",
  "footer.newsletter_desc": "New arrivals & exclusive offers.",
  "footer.email_placeholder": "Email address",
  "footer.join": "Join",
  "footer.shipping_delivery": "Shipping & Delivery",
};

export const I18nProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  // Language is URL-driven (the first path segment). This is what makes each
  // language a distinct, crawlable URL with bot-visible localized content.
  const lang = langFromPath(location.pathname);
  const [strings, setStrings] = useState(EN_STRINGS);
  const [languages, setLanguages] = useState([
    { code: "en", name: "English" },
    { code: "fr", name: "French" },
    { code: "de", name: "German" },
  ]);
  const [loadingLang, setLoadingLang] = useState(false);

  // Keep <html lang> + dir (RTL for Arabic) in sync with the active language.
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang;
      document.documentElement.dir = RTL_LANGS.has(lang) ? "rtl" : "ltr";
    }
  }, [lang]);

  useEffect(() => {
    api.get("/i18n/languages")
      .then((r) => { if (r.data?.languages?.length) setLanguages(r.data.languages); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (lang === "en") {
      setStrings(EN_STRINGS);
      return;
    }
    setLoadingLang(true);
    api.get(`/i18n/ui/${lang}`)
      .then((r) => {
        if (cancelled) return;
        // merge over English so any missing key still renders
        setStrings({ ...EN_STRINGS, ...(r.data?.strings || {}) });
      })
      .catch(() => { if (!cancelled) setStrings(EN_STRINGS); })
      .finally(() => { if (!cancelled) setLoadingLang(false); });
    return () => { cancelled = true; };
  }, [lang]);

  // Switching language navigates to the language-prefixed version of the
  // current path (preserving query). English navigates to the bare path.
  const setLang = useCallback((code) => {
    try { localStorage.setItem(STORAGE_KEY, code); } catch { /* ignore */ }
    const bare = stripLang(location.pathname);
    navigate(withLang(bare, code) + (location.search || ""));
  }, [location.pathname, location.search, navigate]);

  // Localize an internal path for the active language (for <Link to=...>).
  const lp = useCallback((path) => withLang(path, lang), [lang]);

  const t = useCallback((key, fallback) => strings[key] ?? EN_STRINGS[key] ?? fallback ?? key, [strings]);

  // ---- Generic free-text auto-translation (DB-cached server-side) ---- //
  // cache key: `${lang}::${text}` -> translated string
  const [txCache, setTxCache] = useState({});
  const pendingRef = useRef(new Map());   // lang -> Set(text)
  const timerRef = useRef(null);

  const flush = useCallback(() => {
    timerRef.current = null;
    const byLang = pendingRef.current;
    pendingRef.current = new Map();
    byLang.forEach((set, lng) => {
      const texts = Array.from(set).slice(0, 100);
      if (!texts.length) return;
      api.post("/i18n/translate", { lang: lng, texts })
        .then((r) => {
          const tr = r.data?.translations || {};
          setTxCache((prev) => {
            const next = { ...prev };
            texts.forEach((tt) => { next[`${lng}::${tt}`] = tr[tt] ?? tt; });
            return next;
          });
        })
        .catch(() => {
          setTxCache((prev) => {
            const next = { ...prev };
            texts.forEach((tt) => { next[`${lng}::${tt}`] = tt; }); // english fallback (stop re-queue)
            return next;
          });
        });
    });
  }, []);

  const scheduleFlush = useCallback(() => {
    if (timerRef.current) return;
    timerRef.current = setTimeout(flush, 120);
  }, [flush]);

  // tx: translate an arbitrary English string for the active language.
  const tx = useCallback((text) => {
    if (!text || typeof text !== "string") return text;
    if (lang === "en") return text;
    const key = `${lang}::${text}`;
    if (key in txCache) return txCache[key];
    let set = pendingRef.current.get(lang);
    if (!set) { set = new Set(); pendingRef.current.set(lang, set); }
    if (!set.has(text)) { set.add(text); scheduleFlush(); }
    return text; // English fallback until translation arrives
  }, [lang, txCache, scheduleFlush]);

  const value = useMemo(
    () => ({ lang, setLang, lp, t, tx, languages, loadingLang }),
    [lang, setLang, lp, t, tx, languages, loadingLang]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within an I18nProvider");
  return ctx;
};

// Convenience component: <T>English text</T> -> auto-translated for active language.
export const T = ({ children }) => {
  const { tx } = useI18n();
  return <>{typeof children === "string" ? tx(children) : children}</>;
};
